import { Link } from "react-router";
import { ArrowRight } from "lucide-react";
import { useInViewOnce } from "../hooks/useInViewOnce";
import { cn } from "../components/ui/utils";
import { HEADLINE_GRADIENT } from "../components/GradientSectionHeading";
import { BrandLogosMarquee } from "../components/BrandLogosMarquee";
import { FluidCanvas } from "../components/FluidCanvas";

/** Matches files in `public/` (spaces → %20 in URL) */
const PRODUCT_RANGE_IMG = {
  textile: "/products/textile.jpeg",
  garment: "/products/garment.jpeg",
  luxury: "/products/luxury.jpeg",
  label: "/products/label.jpeg",
  custom: "/products/custom.jpeg",
  retail: "/products/retail.jpeg",
  eco: "/products/eco.jpeg",
  business: "/products/business.jpeg",
} as const;

function ProductRangeCard({ 
  to, 
  src, 
  title, 
  description 
}: { 
  to: string; 
  src: string; 
  title: string; 
  description: string; 
}) {
  return (
    <Link 
      to={to} 
      className="group relative overflow-hidden rounded-2xl border border-border bg-white transition-all duration-300 hover:shadow-2xl"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={src}
          alt={`${title} — custom packaging manufacturer Colour Club Printers Faisalabad Pakistan`}
          className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="p-8">
        <h3 className="text-2xl font-semibold mb-3 text-[#131312] transition-colors">
          {title}
        </h3>
        <p className="text-muted-foreground mb-4">
          {description}
        </p>
        <span className="inline-flex items-center font-medium text-[#131312]">
          Explore <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

export function ProductsPage() {
  const { ref: headlineRef, inView: headlineInView } = useInViewOnce({ threshold: 0.28, rootMargin: "0px 0px -10% 0px" });
  const { ref: rangeRef, inView: rangeInView } = useInViewOnce({ threshold: 0.28, rootMargin: "0px 0px -10% 0px" });

  return (
    <div className="min-h-screen">
      {/* HERO SECTION */}
      <section className="relative py-32 bg-[#F6F2EB] overflow-hidden min-h-[768px] flex items-center border-b border-[#131312]/5">
        <FluidCanvas />

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-[#FBF7F4] text-[#131312] border border-[#131312]/10 rounded-full text-sm font-medium mb-8 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.15)]">
              Complete Product Catalog
            </div>
            <h1 ref={headlineRef} className="text-[48px] sm:text-[60px] md:text-[70px] lg:text-[78.1px] font-bold mb-8 leading-tight text-[#1C1C1C]">
              <span
                className={cn("gradient-text-animate inline", headlineInView && "gradient-text-animate--play")}
                style={{ backgroundImage: HEADLINE_GRADIENT }}
              >
                Explore Our Packaging <br />
                Solutions
              </span>
            </h1>
            <p
              className="font-bold leading-[25px] text-[#1C1C1C] mx-auto text-center"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '18.6px',
                maxWidth: '627px',
                letterSpacing: '-0.5px'
              }}
            >
              From insert cards to folding cartons, discover our comprehensive range of premium packaging products designed for global brands.
            </p>
          </div>
        </div>
      </section>

      {/* TRANSITION SPACER */}
      <section className="h-[170px] bg-[#FBFAF7] flex items-center overflow-hidden border-b border-[#131312]/5">
        <div className="w-full max-w-7xl mx-auto px-6 lg:px-8">
          <BrandLogosMarquee />
        </div>
      </section>

      {/* PRODUCTS RANGE SECTION */}
      <section className="py-24 bg-[#F6F2EB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 ref={rangeRef} className="text-5xl font-bold mb-6">
              <span
                className={cn("gradient-text-animate inline", rangeInView && "gradient-text-animate--play")}
                style={{ backgroundImage: HEADLINE_GRADIENT }}
              >
                Our Product Range
              </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive packaging solutions tailored to your industry needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ProductRangeCard 
              to="/products/textile-home-packaging"
              src={PRODUCT_RANGE_IMG.textile}
              title="Textile & Home Packaging"
              description="Custom textile and home packaging solutions designed for bedding, home fabrics, and lifestyle products. We specialize in high-quality insert cards, wraps, and retail-ready packaging that enhances product presentation and brand value."
            />
            <ProductRangeCard 
              to="/products/garment-packaging"
              src={PRODUCT_RANGE_IMG.garment}
              title="Garment Packaging"
              description="Complete garment packaging solutions including labels, tags, and branding elements for fashion and apparel brands, ensuring durability, consistency, and strong brand identity."
            />
            <ProductRangeCard 
              to="/products/luxury-packaging"
              src={PRODUCT_RANGE_IMG.luxury}
              title="Luxury Packaging"
              description="High-end luxury packaging solutions crafted with premium materials and finishes, ideal for perfumes, cosmetics, and exclusive products requiring a refined presentation."
            />
            <ProductRangeCard 
              to="/products/labels-stickers"
              src={PRODUCT_RANGE_IMG.label}
              title="Labels & Stickers"
              description="High-quality labels and stickers for product identification, retail, and logistics, including barcode labels, price stickers, and custom adhesive solutions."
            />
            <ProductRangeCard 
              to="/products/custom-packaging"
              src={PRODUCT_RANGE_IMG.custom}
              title="Custom Packaging"
              description="Fully customized packaging solutions tailored to your product, brand, and industry requirements, covering everything from retail packaging to eCommerce and specialty packaging needs."
            />
            <ProductRangeCard 
              to="/products/retail-display-packaging"
              src={PRODUCT_RANGE_IMG.retail}
              title="Retail & Display Packaging"
              description="Retail and display packaging solutions designed to improve product visibility, optimize shelf space, and increase in-store conversions for retail environments."
            />
            <ProductRangeCard 
              to="/products/eco-friendly-packaging"
              src={PRODUCT_RANGE_IMG.eco}
              title="Eco-Friendly Packaging"
              description="Sustainable packaging solutions using eco-friendly materials and responsible production methods, helping brands reduce environmental impact without compromising quality."
            />
            <ProductRangeCard 
              to="/products/business-print-materials"
              src={PRODUCT_RANGE_IMG.business}
              title="Business Print Materials"
              description="Professional business printing services including marketing materials and corporate stationery to support branding and communication needs."
            />
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#131312] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(72.35% 197.74% at 70% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%),
              radial-gradient(72.35% 197.74% at 30% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%)
            `
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white max-w-4xl mx-auto leading-[1.1]">
            Ready to Create Premium <br /> Packaging?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Get an instant quote and see how we can elevate your brand with custom packaging solutions
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/quote"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-white text-sm font-medium text-[#131312] transition-colors hover:bg-white/90 shadow-lg sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-white/20 bg-white/10 px-8 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:h-14 sm:w-auto sm:text-base"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
